import debounce from 'lodash/debounce';
import { Map, Point, GeolocateControl, NavigationControl, ScaleControl } from 'mapbox-gl';
import type { AnyLayer, AnimationOptions, CameraOptions, GeoJSONSource, LngLatBounds, MapboxGeoJSONFeature } from 'mapbox-gl';

import { Share } from '@capacitor/share';

import GeoService from "./GeoService";
import BaseService from './BaseService';
import { dataTypes, geoPermStatus } from "./Constants";
import LocationData from "../data/LocationData";
import GeoLocation from "../data/GeoLocation";
import MapPosition from "../data/MapPosition";
import SatelliteControl from "../components/SatelliteControl";
import ReportErrorControl from "../components/ReportErrorControl";
import ShareControl from "../components/ShareControl";
import StageControl from "../components/StageControl";
import CardData from "../data/CardData";
import AppService from "./AppService";
import LogManager from "./LogManager";
import CityData from "../data/CityData";
import i18n from "i18next";
import StageData from "../data/StageData";
import CityFinderControl from "../components/CityFinderControl";
import mapboxgl from 'mapbox-gl';
import DeleteDirectionControl from "../components/DeleteDirectionControl";
import { ModalComponentService } from "./ModalComponentService";
import RouteData from "../data/RouteData";
import PermissionsService from './PermissionsService';
import { GronzeGeolocateControl } from "../components/GronzeGeolocateControl";
import VisbilityControl from "../components/VisbilityControl";
import AppLifecycleService from './AppLifecycleService';
import NetworkService from './NetworkService';
import MapFaultInjectionService from '../debug/MapFaultInjectionService';
import MapErrorService from './MapErrorService';
import type MapComponent from '../components/MapComponent';
import type { GeolocateControlInternals } from '../components/GronzeGeolocateControl';
import type { Position } from '@capacitor/geolocation';

/**
 * Capa del estilo de la que se copian `source` y `source-layer`.
 *
 * map.getLayer() devuelve la union AnyLayer, y sus miembros no comparten esos
 * campos aunque las capas del tileset si los tengan.
 */
interface StyleLayerRef {
    type: string;
    source?: string;
    sourceLayer?: string;
}

const mapLayers = {
    // Coming from Mapbox service
    staticCities: 'gronze-localidades',
    staticTracks: 'gronze-tracks',
    satelliteLayer: 'mapbox-satellite',
    mapboxInteractive: 'mapboxgl-interactive',
    // For dynamic and selected items.
    selectedCities: 'selected-cities',
    interactiveCities: 'interactive-localidades',
    selectedTracks: 'selected-tracks',
    interactiveTracks: 'interactive-lines',
    trackArrows: 'track-arrows',
    routeToLocation: 'route-to-location',
    interactivePOIs: 'gronze-pois',
};

const mapDataSources = {
    selectedFeatures: 'selected-features',
    // Just in case we need different datasources.
    selectedCities: 'selected-cities',
    selectedTracks: 'selected-tracks',
}

const orientations = {
    PORTRAIT: 'portrait',
    LANDSCAPE: 'landscape',
}

/**
 * Helper function to debounce map updates.
 */
const debounceUpdateMap = debounce(function () {
    MapService.updateMapDebounced();
}, 200);

/**
 * Provides access to map properties and wraps Mapbox features.
 */
class MapService extends BaseService {

    /** Mapbox map object. */
    static mapObject: Map;

    // The map container DOM element.
    static mapContainer: HTMLElement;
    /** The React Component */
    static mapComponent: MapComponent;

    /** Map is fully loaded. */
    static mapLoaded: boolean;

    /** Current map data. */
    static mapPosition: MapPosition;

    // Map controls
    /**
     * En nativo es GronzeGeolocateControl y en web el de mapbox, pero el codigo
     * usa de los dos los mismos miembros internos (_onSuccess, reset,
     * _clearWatch), que los tipos publicados no declaran.
     */
    static geolocateControl: GeolocateControlInternals;
    static navigationControl: NavigationControl;
    static stageControl: StageControl;
    static shareControl: ShareControl;
    static visibilityControl: VisbilityControl;
    static cityFinderControl: CityFinderControl;
    static satelliteControl: SatelliteControl;
    static reportErrorControl: ReportErrorControl;
    static deleteDirectionControl: DeleteDirectionControl;
    static shouldRestoreGeolocateOnActive = false;
    static wasGeolocatePausedOnBackground = false;
    /** Momento en que el control quedo pausado, o null si no lo esta. */
    static geolocatePausedAt: number | null = null;
    /**
     * Margen para no leer como intencionada la pausa que provoca el propio
     * gesto de salir de la app (ver wasGeolocatePausedByUser).
     */
    static readonly GEOLOCATE_EXIT_GESTURE_MS = 1500;
    static geolocateAppBackgroundHandler = () => {
        this.shouldRestoreGeolocateOnActive = this.isGeolocateTrackingEnabled();
        this.wasGeolocatePausedOnBackground = this.wasGeolocatePausedByUser();
    };
    static geolocateAppActiveHandler = async () => {
        if (!AppService.isNativeApp() || !this.geolocateControl) {
            return;
        }

        // Si no estábamos siguiendo la ubicación al ir a background, no tocamos nada al volver.
        // Esto evita pisar el flujo en curso cuando se acepta el prompt nativo de permisos en el
        // primer click del control (Android pasa por onPause/onResume mostrando el diálogo, y un
        // reset() aquí dejaría _watchState='OFF' justo cuando trigger() está a punto de seguir).
        if (!this.shouldRestoreGeolocateOnActive) {
            return;
        }
        this.shouldRestoreGeolocateOnActive = false;

        // La geolocalizacion se rearma siempre, pero en BACKGROUND cuando ya
        // estaba pausada al irse (card abierta o mapa movido a mano) o cuando
        // al volver hay una card abierta: el watch sigue vivo y el marcador se
        // actualiza, y a cambio la camara no salta al GPS tapando el sitio que
        // el usuario estaba mirando. El seguimiento pleno solo se recupera si
        // era el estado al salir a background.
        const keepCurrentView = this.wasGeolocatePausedOnBackground
            || this.mapComponent?.isCardVisible() === true;
        this.wasGeolocatePausedOnBackground = false;

        try {
            if (typeof this.geolocateControl.reset === 'function') {
                this.geolocateControl.reset();
            }
            if (this.geolocateControl._geolocateButton) {
                this.geolocateControl._geolocateButton.disabled = false;
            }
            // Despues del reset, que es quien lo desarma.
            this.geolocateControl.startInBackground = keepCurrentView;
            if (typeof this.geolocateControl.trigger === 'function') {
                // trigger() puede salir en falso antes de consumir el flag
                // (permiso denegado): desarmar para no degradar la siguiente
                // activacion, que no tiene por que venir de este flujo.
                if (!await this.geolocateControl.trigger()) {
                    this.geolocateControl.startInBackground = false;
                }
            }
        } catch (error) {
            console.warn('MapService geolocateAppActiveHandler error', error);
            this.geolocateControl.startInBackground = false;
        }

        // trigger() emite trackuserlocationstart tambien cuando arranca ya
        // pausado, y ese listener limpia la marca. Se refresca aqui, con el
        // estado final, para que la pausa siga viva en el siguiente ciclo de
        // background: sin esto solo sobreviviria al primero.
        this.geolocatePausedAt = this.isGeolocatePaused() ? Date.now() : null;
    };

    /**
     * @var {string} Selected track id
     */
    static selectedTrackId: string | null;
    static currentTrackId: string | null;
    static renderedStages: StageData[] = [];

    /**
     * @var {string} Selected route id
     */
    static selectedRouteId: string | null;
    static currentRouteId: string | null;
    static renderedRouteNames: RouteData[] = [];

    /**
     * @var {numeric} Selected track zoom
     */
    static selectedTrackZoom: number;

    /**
     * Keep track of selected features.
     */
    static trackFeatures: Record<string, MapboxGeoJSONFeature[]> = {};

    /**
     * Get map object.
     *
     * @return {Map} Mapbox Map
     */
    static getMapObject() {
        return this.mapObject;
    }

    /**
     * Update map with latest map parameters.
     * This is called from debounceUpdateMap().
     */
    static updateMapDebounced() {
        this.debug("Update debounced");
        this.updateMapComponent();

        // If selected features and zoom is bigger, update selection for more detail.
        if (this.selectedTrackZoom) {
            this.showSelectedTrackFeatures();
        }
    }

    /**
     * Update map component with latest map parameters.
     */
    static updateMapComponent() {
        this.debug("Update map component");
        this.captureMap();
        this.setLayerVisibility();
        this.mapComponent.mapEventChanged(this.mapPosition);
    }

    /***
     * Gets current map location and zoom
     * @return {LocationData}
     */
    static getMapPosition() {
        // const location = this.getMapObject().getCenter();
        return new LocationData(this.mapPosition.center.lat, this.mapPosition.center.lng, this.mapPosition.zoom);
    }

    /**
     * Handle some map events.
     */
    static async mapEventMoved(event: mapboxgl.MapboxEvent<MouseEvent | TouchEvent | WheelEvent | undefined>): Promise<void> {
        const map = event.target;
        this.updateFromMap(map);
        this.debug("Map moved to ", this.mapPosition.center);
        debounceUpdateMap();
    }

    static mapEventZoom(event: mapboxgl.MapboxEvent<MouseEvent | TouchEvent | WheelEvent | undefined>): void {
        const map = event.target;
        this.updateFromMap(map);
        this.debug("Map zoom to ", this.mapPosition.zoom);
        debounceUpdateMap();
    }

    static mapEventRotate(event: mapboxgl.MapboxEvent<MouseEvent | TouchEvent | WheelEvent | undefined>): void {
        const map = event.target;
        this.updateFromMap(map);
        const compassButton = document.querySelector('.mapboxgl-ctrl-compass');
        if (compassButton) compassButton.classList.toggle('show', this.mapPosition.bearing !== 0);
        this.debug("Map rotate to ", this.mapPosition.bearing);
        debounceUpdateMap();
    }

    /**
     * Handles StageControl click.
     */
    static mapEventStageControlClick() {
        this.mapComponent.mapEventClickStageControl();
        // this.showSelectedTrackFeatures();
    }

    /**
     * Handles ShareControl click.
     */
    static async mapEventShareControlClick() {
        const location = await GeoService.getCurrentLocation();
        if (location) {
            Share.share({
                text: i18n.t('share.location'),
                url: `https://maps.google.com/?q=${location.lat},${location.lng}`,
            })
                .catch(() => {
                    this.debug("Error compartiendo ubicación o compartir cancelado");
                });
        }
    }

    // Map load event.
    static async mapEventLoad() {
        // Add interactive layers
        // this.createMapLayers(this.mapObject);

        // Set event handlers
        this.mapObject.on('moveend', async (event) => {
            this.mapEventMoved(event);
        });

        this.mapObject.on('zoomend', (event) => {
            this.mapEventZoom(event);
        });

        this.mapObject.on('rotateend', (event) => {
            this.mapEventRotate(event);
        });
    }

    /**
     * Set map as loaded (from load event)
     */
    static setMapLoaded() {
        this.setLayerVisibility();
        this.mapLoaded = true;
        this.captureMap();
    }

    /**
     * Capture map position and zoom to use in next sessions
     */
    static captureMap() {
        const map = this.getMapObject();
        this.updateFromMap(map);
        GeoService.storeLocation(this.getMapPosition());
    }

    /**
     * Update location from map.
     */
    static updateFromMap(map: Map): void {
        this.mapPosition = MapPosition.fromMap(map);
    }

    /**
     * Destroy map handlers to remove map from memory
     *
     * Parameters:
     */
    static destroyMap() {
        if (this.mapObject) {
            this.mapObject.remove();
        }
    }

    /**
     * Change permission status handler fired from permissions service
     *
     * Parameters:
     * - value: permission status. One of geoPermStatus.
     */
    static changePermissionHandler(value: string): void {
        this.debug('MapService.changePermissionHandler', value);
        if (value === geoPermStatus.granted) {
            this.debug("MapService.changePermissionHandler granted, resetting control");
            if (this.geolocateControl && this.geolocateControl._geolocateButton) {
                this.geolocateControl._geolocateButton.disabled = false;
            }
        } else if (value === geoPermStatus.denied && this.geolocateControl && typeof this.geolocateControl.reset === 'function') {
            console.log('MapService.changePermissionHandler denied, resetting control');
            this.geolocateControl.reset();
        }
    }

    /**
     * Returns true when the geolocation control was tracking the user.
     *
     * @returns {boolean}
     */
    static isGeolocateTrackingEnabled() {
        if (!this.geolocateControl) {
            return false;
        }

        const watchState = this.geolocateControl._watchState;
        const isButtonPressed = this.geolocateControl._geolocateButton?.getAttribute('aria-pressed') === 'true';

        return isButtonPressed || [
            'WAITING_ACTIVE',
            'ACTIVE_LOCK',
            'ACTIVE_ERROR',
            'BACKGROUND',
            'BACKGROUND_ERROR',
        ].includes(watchState);
    }

    /**
     * Returns true when the geolocation control is in the intermediate state:
     * the watch is alive and the marker keeps updating, but the camera no
     * longer follows the user because a card was opened or the map was panned.
     *
     * @returns {boolean}
     */
    static isGeolocatePaused(): boolean {
        if (!this.geolocateControl) {
            return false;
        }

        return [
            'BACKGROUND',
            'BACKGROUND_ERROR',
        ].includes(this.geolocateControl._watchState);
    }

    /**
     * True cuando el control esta pausado y ya lo estaba antes de que el
     * usuario empezase a salir de la app.
     *
     * Hace falta el margen porque el gesto de irse a background (deslizar
     * desde el borde inferior hacia el home o el selector de apps) empieza
     * como un arrastre sobre el mapa: ese movestart degrada el control a
     * BACKGROUND justo antes de que llegue el appStateChange. Sin el, toda
     * salida por gesto se leeria como una pausa intencionada y la
     * geolocalizacion no volveria a activarse nunca.
     *
     * @returns {boolean}
     */
    static wasGeolocatePausedByUser(): boolean {
        if (!this.isGeolocatePaused() || this.geolocatePausedAt === null) {
            return false;
        }

        return Date.now() - this.geolocatePausedAt > this.GEOLOCATE_EXIT_GESTURE_MS;
    }

    /**
     * Prevent geolocation from being re-armed when the app returns to the
     * foreground (see geolocateAppActiveHandler).
     *
     * Used on deep-link navigation: instead of turning geolocation off, we let
     * the upcoming map movement demote the tracking control from ACTIVE_LOCK to
     * BACKGROUND on its own (same behaviour as selecting a locality), so the
     * camera stops following the user without stopping the location watch.
     */
    static suppressGeolocateRestore() {
        this.shouldRestoreGeolocateOnActive = false;
    }

    /**
     * Create Mapbox Map.
     *
     * @param {Component} mapComponent
     * @param {Reference} mapContainer
     * @param {Array} mapCenter {lon, lat}
     * @param {number} [mapZoom]
     * @param {number} [minZoom]
     */
    static createMap(mapComponent: MapComponent, mapContainer: HTMLElement, mapCenter: [number, number], mapZoom?: number, minZoom?: number): Map {
        this.debug('createMap, center', mapCenter);

        // Store / create objects.
        this.mapComponent = mapComponent;
        this.mapContainer = mapContainer;

        // Store some initial data.
        this.mapPosition = new MapPosition(GeoLocation.convert(mapCenter), mapZoom);

        // Go for the Mapbox Map
        const app_config = this.config();
        // config.ACCESS_TOKEN es interno del runtime de mapbox-gl y no esta
        // declarado en @types/mapbox-gl.
        (mapboxgl as unknown as { config: { ACCESS_TOKEN: string } }).config.ACCESS_TOKEN = app_config.mapbox_access_token;

        this.mapObject = new Map({
            container: this.mapContainer,
            style: app_config.mapbox_style,
            center: mapCenter,
            zoom: mapZoom,
            minZoom,
            preserveDrawingBuffer: true,
            maxBounds: GeoService.getMapBounds() as [[number, number], [number, number]],
            // Solo pruebas: undefined salvo que config active debug_map_fault_injection.
            transformRequest: MapFaultInjectionService.getTransformRequest(),
        });

        MapErrorService.watch(this.mapObject, {
            isOffline: () => NetworkService.isOffline(),
            isInBackground: () => AppLifecycleService.isInBackground(),
        });

        // Map loaded, register some more events.
        /*
        this.mapObject.on('load', async () => {
            this.debug("MapService: Map loaded");
            this.mapEventLoad(this.mapObject);
            this.mapComponent.mapEventLoad(this.mapObject);
        });
        */

        return this.mapObject;
    }

    /**
     * Create Map Controls.
     *
     * Triggers on map.load
     */
    static async createMapControls(map: Map): Promise<void> {
        this.debug("createMapControls");
        
        // Helper function to prevent control clicks from closing the card
        // Los controles de mapbox no publican _container en sus tipos.
        const preventControlPropagation = (controlElement: unknown) => {
            const container = (controlElement as { _container?: HTMLElement })?._container;
            if (container) {
                container.addEventListener('click', (e: Event) => e.stopPropagation());
                container.addEventListener('touchend', (e: Event) => e.stopPropagation());
            }
        };
        
        const geolocationControlOptions = {
            positionOptions: {
                enableHighAccuracy: true
            },
            trackUserLocation: true,
            showUserHeading: true,
        };
        // En iOS nativo (Capacitor) el GeolocateControl estándar puede marcarse como "disabled"
        // (icono con diagonal) porque depende de navigator.geolocation en un esquema tipo capacitor://.
        // Usamos el control custom que delega en @capacitor/geolocation.
        if (AppService.isNativeApp()) {
            this.geolocateControl = new GronzeGeolocateControl(geolocationControlOptions);
        } else {
            this.geolocateControl = new GeolocateControl(geolocationControlOptions) as unknown as GeolocateControlInternals;
        }

        // Fecha en que el control entra y sale del estado pausado, que es lo
        // que geolocateAppBackgroundHandler necesita para decidir si al volver
        // se recupera el seguimiento o se respeta la pausa.
        this.geolocateControl.on('trackuserlocationstart', () => {
            this.geolocatePausedAt = null;
        });
        this.geolocateControl.on('trackuserlocationend', () => {
            this.geolocatePausedAt = this.isGeolocatePaused() ? Date.now() : null;
        });

        // we need to overwrite the success geocontrol function to close logs after click
        const geoControlSuccess = this.geolocateControl._onSuccess.bind(this);
        this.geolocateControl._onSuccess = (position) => {
            ModalComponentService.reset(null, ModalComponentService.ModalComponentTypes.PERMISSIONS_CARD);
            LogManager.closeLog();
            this.verifyIfOutside(position);
            geoControlSuccess(position);
        }

        this.geolocateControl.on('trackuserlocationstart', async () => {
            // Si usamos el control custom (apps nativas), el propio trigger ya gestiona permisos/watch.
            if (AppService.isNativeApp()) {
                return;
            }
            if (await PermissionsService.ensureGeoPermission()) {
                this.showShareLocation(true);
                return;
            }
            // ahora necesito que el botón esté activo para cuando se vuelva de permisos
            // ahora necesito que el botón esté activo para cuando se vuelva de permisos
            if (typeof this.geolocateControl.reset === 'function') {
                console.log('MapService.createMapControls trackuserlocationstart, resetting control');
                this.geolocateControl.reset();
            } else {
                // Fallback for standard control or if reset not defined
                const button = this.geolocateControl._geolocateButton;
                if (button) {
                    button.disabled = false;
                    button.classList.remove(
                        'mapboxgl-ctrl-geolocate-waiting',
                        'mapboxgl-ctrl-geolocate-active',
                        'mapboxgl-ctrl-geolocate-active-error',
                        'mapboxgl-ctrl-geolocate-background',
                        'mapboxgl-ctrl-geolocate-background-error'
                    );
                    button.setAttribute('aria-pressed', 'false');
                }
                if (this.geolocateControl._geolocationWatchID !== undefined) {
                    this.geolocateControl._clearWatch();
                }
                this.geolocateControl._watchState = 'OFF';
            }
        });

        this.geolocateControl.on('trackuserlocationstart', async () => {
            this.showShareLocation(true);
        });
        this.geolocateControl.on('trackuserlocationend', () => {
            this.showShareLocation(false);
        });

        map.addControl(this.geolocateControl);
        preventControlPropagation(this.geolocateControl);

        // En nativo, al volver del segundo plano el watch de ubicación puede detenerse y el control
        // quedarse desincronizado. Guardamos si estaba activo al salir y lo rearmamos automáticamente al volver.
        if (AppService.isNativeApp()) {
            AppLifecycleService.addAppInactiveHandler(this.geolocateAppBackgroundHandler);
            AppLifecycleService.addAppActiveHandler(this.geolocateAppActiveHandler);
        }

        this.cityFinderControl = new CityFinderControl();
        map.addControl(this.cityFinderControl);
        preventControlPropagation(this.cityFinderControl);

        this.visibilityControl = new VisbilityControl();
        map.addControl(this.visibilityControl);
        preventControlPropagation(this.visibilityControl);

        this.satelliteControl = new SatelliteControl();
        map.addControl(this.satelliteControl);
        preventControlPropagation(this.satelliteControl);

        this.reportErrorControl = new ReportErrorControl();
        map.addControl(this.reportErrorControl);
        preventControlPropagation(this.reportErrorControl);

        this.stageControl = new StageControl();
        map.addControl(this.stageControl);
        preventControlPropagation(this.stageControl);

        this.shareControl = new ShareControl();
        map.addControl(this.shareControl);
        preventControlPropagation(this.shareControl);

        this.navigationControl = new NavigationControl({
            showCompass: true,
            showZoom: true,
        });
        map.addControl(this.navigationControl);
        preventControlPropagation(this.navigationControl);

        const scaleControl = new ScaleControl({
            maxWidth: 100,
            unit: 'metric'
        });
        map.addControl(scaleControl);
        preventControlPropagation(scaleControl);

        this.deleteDirectionControl = new DeleteDirectionControl();
        map.addControl(this.deleteDirectionControl);
        preventControlPropagation(this.deleteDirectionControl);
    }

    /**
     * Create Map Layers.
     *
     * Triggers on map.load
     */
    static async createMapLayers(map: Map): Promise<void> {
        this.debug("createMapLayers");
        const config = this.config();

        // See all layers in the map style, for debugging
        // this.debug("All layers", map.getStyle().layers)

        // Places (Localidades), add interactive layer *************************
        let trackLayer = map.getLayer(mapLayers.staticCities) as unknown as StyleLayerRef;

        map.addLayer({
            'id': mapLayers.interactiveCities,
            'type': trackLayer.type,
            'source': trackLayer.source,
            'source-layer': trackLayer.sourceLayer,
            'paint': {
                'circle-radius': 12,
                'circle-color': 'rgba(0,0,0,0)',
            },
            'minzoom': config.zoom_level_place_min,
        } as AnyLayer,
            mapLayers.staticCities);

        // add empty layer to show higlitgthed locations
        map.addSource(mapDataSources.selectedCities, {
            type: 'geojson',
            data: {
                "type": "FeatureCollection",
                "features": []
            }
        });

        map.addLayer({
            'id': mapLayers.selectedCities,
            'type': trackLayer.type,
            'source': mapDataSources.selectedCities,
            'paint': {
                'circle-radius': 14,
                'circle-color': 'rgba(256,0,0,0.5)',
            },
            'minzoom': config.zoom_level_place_min,
            'maxzoom': config.zoom_level_place_max
        } as AnyLayer,
            mapLayers.staticCities);

        // Tracks, add click controller *******************************
        trackLayer = map.getLayer(mapLayers.staticTracks) as unknown as StyleLayerRef;

        map.setPaintProperty(
            mapLayers.staticTracks,
            'line-dasharray',
            [
                'case',
                ['==', ['get', 'variante'], true], // Si la propiedad "variante" es true
                [1.6, 0.3], // Establece la línea punteada
                [1, 0] // Si no es true, establece una línea continua (o cualquier otro estilo)
            ]
        );

        map.addLayer({
            id: mapLayers.trackArrows,
            type: trackLayer.type,
            source: trackLayer.source,
            'source-layer': trackLayer.sourceLayer,
            paint: {
                'line-width': 12,
                'line-pattern': 'arrow',
                'line-join': 'round',
                'line-opacity': 1,
            },
            // el filtro quita las flechas de las líneas punteadas (variantes)
            // 'filter': ['==', 'variante', false],
        } as AnyLayer)

        map.addLayer({
            'id': mapLayers.interactiveTracks,
            'type': trackLayer.type,
            'source': trackLayer.source,
            'source-layer': trackLayer.sourceLayer,
            'paint': {
                'line-width': 30,
                'line-color': 'rgba(0,0,0,0)',
            },
        } as AnyLayer,
            mapLayers.staticTracks);

        map.addSource(mapDataSources.selectedTracks, {
            'type': 'geojson',
            'data': {
                'type': 'FeatureCollection',
                'features': []
            }
        });

        map.addLayer({
            'id': mapLayers.selectedTracks,
            'type': trackLayer.type,
            'source': mapDataSources.selectedTracks,
            'paint': {
                'line-width': 14,
                // @todo Style may be based on the existing track's color
                // Use a get expression (https://docs.mapbox.com/mapbox-gl-js/style-spec/#expressions-get)
                // to set the line-color to a feature property value.
                'line-color': 'rgba(255,0,0,0.5)',
            }
        } as AnyLayer, mapLayers.staticTracks);

        this.debug("Added map layers and data sources");
    }

    /**
     * Gestiona la visibilidad de las capas interactivas dependiendo del zoom.
     * - After map loaded
     * - After map zoom changes
     */
    static setLayerVisibility() {
        const map = this.getMapObject();
        const zoom = map.getZoom();
        this.debug("Set layer visibility for zoom", zoom);

        const way_visibility = (this.currentTrackId || GeoService.showCamino(zoom) || GeoService.showStages(zoom)) ? 'visible' : 'none';
        const locs_visibility = GeoService.showPlaces(zoom) ? 'visible' : 'none';
        const arrows_visibility = GeoService.showArrows(zoom) ? 'visible' : 'none';

        map.setLayoutProperty(mapLayers.interactiveTracks, 'visibility', way_visibility);
        map.setLayoutProperty(mapLayers.interactiveCities, 'visibility', locs_visibility);
        map.setLayoutProperty(mapLayers.trackArrows, 'visibility', arrows_visibility);
    }

    /**
     * Show/hide satelite layer
     *
     * @param {boolean} visible
     */
    static setSatelliteVisibility(visible: boolean): void {
        this.getMapObject().setLayoutProperty(mapLayers.satelliteLayer, 'visibility', visible ? 'visible' : 'none');
    }

    /**
     * clean selected tracks and cities, but keep current track.
     */
    static cleanSelectedFeatures() {
        this.setSelectedRouteId(null, false);
        this.setSelectedTrackId(null, false);
        MapService.showSelectedTrackFeatures();
        MapService.setSelectedPlaces();
    }

    static selectTrackNumber() {
        const selectedNumberClass = 'selected-number';
        const numbers = document.querySelectorAll(`.${selectedNumberClass}`);
        numbers.forEach(element => element.classList.remove(selectedNumberClass));

        if (!this.selectedTrackId)
            return;

        document.getElementById(this.selectedTrackId)?.classList.add(selectedNumberClass);
    }

    static selectRouteName() {
        const selectedNameClass = 'selected-name';
        const names = document.querySelectorAll(`.${selectedNameClass}`);
        names.forEach(element => element.classList.remove(selectedNameClass));

        if (!this.selectedRouteId)
            return;

        document.getElementById(this.selectedRouteId)?.classList.add(selectedNameClass);
    }

    /**
     * Sets / unsets selected track id.
     * @param {array} ids Track id or null to unselect
     */
    static setSelectedTrackId(id: string | null = null, show = true): void {
        this.debug("Selected track id", id);
        // Skip if not changed.
        if (this.selectedTrackId === id) return;

        if (id) {
            this.setSelectedRouteId(null, false);
            // If same as current track, do not add anything.
            if (this.currentTrackId === id) {
                id = null;
            }
        } else if (this.selectedTrackId) {
            // Clean up previous data.
            delete this.trackFeatures[this.selectedTrackId];
        }

        // en caso de tener un valor 0 número letra al final, quito el cero
        if (id) {
            const regex = /\b.*-0\d[a-zA-Z]\b/;
            if (id.match(regex)) {
                id = id.replace(/-0(\d)([a-zA-Z]?)/g, function (_match: string, numero: string, letra: string) {
                    return "-" + numero + letra;
                });
            }
        }
        this.selectedTrackId = id;
        this.selectTrackNumber();

        if (show) this.showSelectedTrackFeatures();
    }

    /**
     * Sets / unsets current track id.
     * @param {string} id Track id or null to unselect
     */
    static setSelectedRouteId(id: string | null = null, show = true): void {
        this.debug("Selected route id", id);
        // Skip if not changed.
        if (this.selectedRouteId === id) return;

        if (id) {
            this.setSelectedRouteId(null, false);
            // If same as current track, do not add anything.
            if (this.currentRouteId === id) {
                id = null;
            }
        } else if (this.selectedRouteId) {
            // Clean up previous data.
            delete this.trackFeatures[this.selectedRouteId];
        }

        this.selectedRouteId = id;
        this.selectRouteName();

        if (show) this.showSelectedTrackFeatures();
    }

    /**
     * Sets / unsets current track id.
     *
     * @param cardData Carta cuya etapa pasa a ser la actual.
     */
    static setCurrentTrackId(cardData: CardData, show = true): void {
        const id = cardData.getMapId();
        this.debug("Current track id", id);

        if (this.currentTrackId === id) {
            this.currentTrackId = null;
            this.stageControl.setVisible(false);
        } else {
            if (id) {
                // Reset other tracks.
                this.selectedRouteId = null;
                this.selectedTrackId = null;
                this.stageControl.setVisible(true);
            }
            else if (this.currentTrackId) {
                // Clean up previous data.
                delete this.trackFeatures[this.currentTrackId];
                this.stageControl.setVisible(false);
            }

            this.currentTrackId = id ?? null;

            if (show) this.showSelectedTrackFeatures();
        }

        // force center on stage
        cardData.clickPoint = null;
        this.relocateMapBeforeDetail(cardData);
    }

    /**
     * Set track features to show on map.
     *
     * The currently selected tracks will be added automatically.
     */
    static showSelectedTrackFeatures() {
        if (NetworkService.isOffline()) {
            return;
        }

        let extra: MapboxGeoJSONFeature[] = [];
        let features: MapboxGeoJSONFeature[] = [];

        if (this.selectedTrackId) {
            extra = this.getTrackFeatures(this.selectedTrackId);
            features = features.concat(extra);
            this.debug("Selected features " + this.selectedTrackId, extra);
        }
        if (this.currentTrackId) {
            extra = this.getTrackFeatures(this.currentTrackId);
            features = features.concat(extra);
            this.debug("Current track features " + this.currentTrackId, extra);
        }
        if (this.selectedRouteId) {
            extra = this.getRouteFeatures(this.selectedRouteId);
            features = features.concat(extra);
            this.debug("Route features " + this.selectedRouteId, extra);
        }
        if (this.currentRouteId) {
            extra = this.getTrackFeatures(this.currentRouteId);
            features = features.concat(extra);
            this.debug("Current route features " + this.currentRouteId, extra);
        }
        this.selectedTrackZoom = features.length ? this.mapPosition.zoom ?? 0 : 0;
        this.setSelectedFeatures(features, mapDataSources.selectedTracks);
    }

    /**
     * Set selected city features to show on map.
     */
    static setSelectedPlaces(features: MapboxGeoJSONFeature[] = []): void {
        this.setSelectedFeatures(features, mapDataSources.selectedCities);
    }

    /**
     * Set features (cities) to show on map.
     */
    static setSelectedFeatures(features: MapboxGeoJSONFeature[] = [], dataSource: string): void {
        (this.getMapObject().getSource(dataSource) as GeoJSONSource).setData({
            "type": "FeatureCollection",
            features
        });
    }

    /**
     * Calculate new map center to display location.
     *
     * @todo This fails is center is not inside current bounds.
     *
     * @param {LocationData} pointLocation
     * @param {size} size of card
     * @param {force} force center using the point provided
     * @return {GeoLocation}
     */
    static getTranslationToCenter(pointLocation: { lng: number; lat: number }, size: string, force?: boolean) {
        const map = this.getMapObject();
        const classList = document.documentElement.classList;
        const orientation = classList.contains('orientation_portrait') ? orientations.PORTRAIT : orientations.LANDSCAPE;
        const center = map.getCenter();
        const longitude = pointLocation.lng;
        const latitude = pointLocation.lat;
        const { lng, lat } = center;
        const markerPixels = map.project({ lat: latitude, lng: longitude });
        let newCords;
        if (orientation === orientations.PORTRAIT && (force || latitude < lat)) {
            const desvY = size === CardData.BIG ? 4 : 6;
            newCords = map.unproject(new Point(markerPixels.x, markerPixels.y + window.innerHeight / desvY));
        } else if (orientation === orientations.LANDSCAPE && (force || longitude < lng)) {
            newCords = map.unproject(new Point(markerPixels.x - window.innerWidth / 4, markerPixels.y));
        } else {
            return null;
        }
        return new GeoLocation(newCords.lng, newCords.lat);
    }

    /**
     * Calculate new map center to display tracks.
     *
     * @param {bounds} Geolocation bounds
     * @param {size} CardData.BIG or CardData.small
     * @return {GeoLocation}
     */
    static getTrackPosition(bounds: LngLatBounds, size: string) {
        const map = this.getMapObject();
        const classList = document.documentElement.classList;
        const orientation = classList.contains('orientation_portrait') ? orientations.PORTRAIT : orientations.LANDSCAPE;
        const topRight = map.project(bounds._ne);
        const bottomLeft = map.project(bounds._sw);
        const markerPixels = map.project({ lat: bounds.getCenter().lat, lng: bounds.getCenter().lng });
        const desvY = size === CardData.BIG ? 2 : 2 / 3;
        if (orientation === orientations.PORTRAIT) {
            if (bottomLeft.x < 0 ||
                topRight.x > window.innerWidth ||
                topRight.y < 0 ||
                bottomLeft.y > window.innerHeight / desvY
            ) {
                const desvPos = CardData.BIG ? 4 : 6;
                return map.unproject(new Point(markerPixels.x, markerPixels.y + window.innerHeight / desvPos));
            }
        } else if (orientation === orientations.LANDSCAPE) {
            if (bottomLeft.x < window.innerWidth / 2 ||
                topRight.x > window.innerWidth ||
                topRight.y < 0 ||
                bottomLeft.y > window.innerHeight
            ) {
                return map.unproject(new Point(markerPixels.x - window.innerWidth / 4, markerPixels.y));
            }
        }
        return null;
    }

    /**
     * Relocate map if needed to maintain clicked marker visible. Set max zoom
     *
     * @param {PlaceData} placeData
     * @param {CardData} cardData
     */
    static relocateMapBeforeDetail(cardData: CardData): void {
        const map = this.getMapObject();
        let centerPoint, newLocation, newZoom;
        if (cardData.clickPoint) {
            newLocation = this.getTranslationToCenter(cardData.clickPoint, cardData.getDetailCardSize(), true);
        }
        if (!newLocation) {
            if (cardData.getGeoBounds()) {
                centerPoint = cardData.getGeoBounds()!.getCenter();
                newLocation = this.getTrackPosition(cardData.getGeoBounds()!, cardData.getDetailCardSize());
                if (!newLocation) {
                    if (map.getBounds().contains(cardData.getGeoBounds()!.getCenter())) {
                        newLocation = this.getTranslationToCenter(centerPoint, cardData.getDetailCardSize());
                    } else {
                        newLocation = centerPoint;
                    }
                }
            } else {
                if (!cardData.geo_location) {
                    this.debug(`Problem looking for geo_location value on "${cardData.getTitle()}": ${cardData.getUrl()}; cancel map position and zoom relocating!`);
                    return;
                }
                centerPoint = newLocation = cardData.getGeoLocation()!;
                if (map.getBounds().contains(centerPoint)) {
                    newLocation = this.getTranslationToCenter(centerPoint, cardData.getDetailCardSize());
                } else {
                    newLocation = centerPoint;
                }
            }
        }

        const options: AnimationOptions & CameraOptions = { duration: 1000 };
        // Adjust zoom if needed. I.e. we fly to stage from a hotel,
        // then zoom is too big to see stage.
        // El tipo de la carta solo se conoce en runtime: los que no tienen
        // rango de visibilidad (track, page) se saltan el ajuste, que es lo
        // que el guard de abajo ya pretendia.
        const zoom = GeoService.isZoomRangeType(cardData.type)
            ? GeoService.getTypeZoomLevels(cardData.type)
            : null;
        if (zoom && map.getZoom() > zoom.max && zoom.max > map.getZoom()) {
            this.debug("relocate/Zoom", zoom);
            newZoom = zoom.max;
            options.zoom = newZoom;
        }
        if (newLocation) {
            this.debug('relocateMap to Bounds center', centerPoint);
            if (newZoom && newZoom > map.getZoom()) {
                options.zoom = newZoom
            }
            map.panTo(newLocation, options);
        } else if (newZoom && newZoom > map.getZoom()) {
            map.zoomTo(newZoom, options);
        }
    }

    /**
     * Move map to given geolocation at place zoom level
     */
    static panToLocation(newLocation: number[]): void {
        const map = this.getMapObject();
        const options: AnimationOptions & CameraOptions = { duration: 1000 };
        const zoom = GeoService.getTypeZoomLevels('place');
        if (zoom && (map.getZoom() < zoom.max || map.getZoom() > zoom.min)) {
            this.debug("relocate/Zoom", zoom);
            options.zoom = zoom.max + 2;
        }
        map.panTo(newLocation as [number, number], options);
    }

    /**
     * Set the map zoom to the max stage zoon level
     */
    static zoomToStageLevel() {
        const zoom = GeoService.getTypeZoomLevels(dataTypes.stage);
        const duration = 700;
        const options = { duration };
        const map = this.getMapObject();
        map.zoomTo(zoom.max, options);
        // this timeout is needed to await for the zoom animation end
        return new Promise(resolve => setTimeout(resolve, duration));
    }

    /**
     * Get map features for city.
     *
     * @param {string} city_id
     */
    static getCityFeatures(city_id: string | number) {
        const options = {
            layers: [mapLayers.interactiveCities], // mapLayers.staticTracks],
            filter: ['==', ['get', 'id'], city_id],
        }
        return this.getMapObject().queryRenderedFeatures(undefined, options);
    }

    /**
     * Get map features for track.
     *
     * @param {string} route_id
     */
    static getRouteFeatures(route_id: string | number) {
        const options = {
            layers: [mapLayers.interactiveTracks], // mapLayers.staticTracks],
            filter: ['==', ['get', 'route_id'], route_id],
        }
        return this.getMapObject().queryRenderedFeatures(undefined, options);
    }

    /**
     * Get map features for track.
     *
     * @param {string} track_id
     */
    static getTrackFeatures(track_id: string | number) {
        const options = {
            layers: [mapLayers.interactiveTracks], // mapLayers.staticTracks],
            filter: ['==', ['get', 'track_id'], track_id],
        }

        let features = this.getMapObject().queryRenderedFeatures(undefined, options);
        // Maybe features are not visible, if so use stored ones.
        if (features.length) {
            this.trackFeatures[track_id] = features;
        }
        else if (track_id in this.trackFeatures) {
            features = this.trackFeatures[track_id];
        }
        return features;
    }

    static verifyIfOutside(position: Position): void {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;
        const map_bounds = GeoService.getMapBounds();
        if (lon < map_bounds[0][0] ||
            lon > map_bounds[1][0] ||
            lat < map_bounds[0][1] ||
            lat > map_bounds[1][1]
        ) {
            ModalComponentService
                .openModalComponent(ModalComponentService.ModalComponentTypes.OUT_OF_MAP_CARD);
            this.geolocateControl._clearWatch();
        }
    }

    static gotoPlace(location: { id: number | string; name: string; location: { lng: number; lat: number }; zoom: number }): void {
        const placeData = new CityData(dataTypes.place, location.id);
        placeData.name = location.name;
        this.mapObject.flyTo({
            center: [location.location.lng, location.location.lat],
            zoom: location.zoom
        });
    }

    static showShareLocation(geolocationStatus: boolean): void {
        if (!AppService.canShare) {
            this.debug("This device can't share: don't show share button");
            return;
        }
        this.shareControl.setVisible(geolocationStatus);
    }

    static showDeleteDirection(showDirection: boolean): void {
        this.deleteDirectionControl.setVisible(showDirection);
        if (!showDirection) {
            LogManager.unlock();
        }
    }

    static createTrackNumbers() {
        this.deleteTrackNumbers();

        const map = this.getMapObject();
        // get features rendered on actual bounds
        const zoom = map.getZoom();
        if (!GeoService.showNumbers(zoom)) {
            return;
        }
        const options = {
            layers: [mapLayers.staticTracks],
        }
        const features = map.queryRenderedFeatures(undefined, options);
        const points = features.filter(feature => feature.geometry.type === 'Point' && feature.properties!.name.toLowerCase() === 'inicio');
        points
            .forEach(feature => {
                if (feature.properties!.name.toLowerCase() === 'inicio') {
                    const trackNumbers = this.renderedStages.find((stageData) => stageData.track_id === feature.properties!.track_id);
                    if (!trackNumbers) {
                        // El feature es un punto: lo garantiza el filter de arriba.
                        const stageData = StageData.fromMapFeature(feature as unknown as Parameters<typeof StageData.fromMapFeature>[0]);
                        stageData.createMapMarker((feature.layer.paint as Record<string, unknown>)['line-color'] as string);
                        stageData.addToMap(map);
                        this.renderedStages.push(stageData);
                    }
                }
            });

        this.selectTrackNumber();
    }

    static createRouteNames() {
        this.deleteRouteNames();

        const map = this.getMapObject();
        // get features rendered on actual bounds
        const zoom = map.getZoom();
        if (!GeoService.showRouteNames(zoom)) {
            return;
        }

        const options = {
            layers: [mapLayers.staticTracks],
        }
        const features = map.queryRenderedFeatures(undefined, options);
        const points = features.filter(feature => feature.geometry.type === 'Point');
        points
            .forEach(feature => {
                if (feature.properties!.name.indexOf('Camino:') === 0) {
                    const routeNames = this.renderedRouteNames.find(routeData => routeData.name === feature.properties!.name);
                    if (!routeNames) {
                        // El feature es un punto: lo garantiza el filter de arriba.
                        const routeData = RouteData.fromMapFeature(feature as unknown as Parameters<typeof RouteData.fromMapFeature>[0]);
                        routeData.createMapMarker((feature.layer.paint as Record<string, unknown>)['line-color'] as string);
                        routeData.addToMap(map);
                        this.renderedRouteNames.push(routeData);
                    }

                }
            });

        this.selectRouteName();
    }

    static deleteTrackNumbers() {
        this.renderedStages.forEach(stageData => stageData.removeMarker());
        this.renderedStages = [];
    }

    static deleteRouteNames() {
        this.renderedRouteNames.forEach(routeData => routeData.removeMarker());
        this.renderedRouteNames = [];
    }

    static deleteWalkingRoute() {
        const map = this.mapObject;
        if (map.getSource(mapLayers.routeToLocation)) {
            map.removeLayer(mapLayers.routeToLocation);
            map.removeSource(mapLayers.routeToLocation);
        }
        this.showDeleteDirection(false);
    }

    static async createRouteToLocationLayer(geometry: GeoJSON.Geometry): Promise<void> {
        const map = this.mapObject;
        this.deleteWalkingRoute();

        map.addLayer({
            id: mapLayers.routeToLocation,
            type: 'line',
            source: {
                type: 'geojson',
                data: {
                    type: 'Feature',
                    properties: {},
                    geometry: geometry
                }
            },

            layout: {
                'line-cap': 'round',
                'line-join': 'round',
            },
            paint: {
                'line-width': 6,
                'line-opacity': 1,
                'line-color': 'rgb(11,105,250)',
                'line-dasharray': [0, 3],
            },

        } as AnyLayer);

        this.showDeleteDirection(true);
    }

    static createDirectionInfo(distance: number, duration: number): void {
        const minutes = Math.round(duration / 60);
        // toFixed devuelve cadena; las comparaciones de abajo la convertian de
        // forma implicita.
        const meters = distance.toFixed(0);
        let metersText = `${meters} m`;
        if (Number(meters) >= 1000) {
            const formatedMeters = (Number(meters) / 1000)
                .toFixed(1)
                .replace('.', ',');
            metersText = `${formatedMeters} km`;
        }

        setTimeout(() => {
            LogManager.createDirectionInfo(`${i18n.t('directionToast.distance')} ${metersText} | ${i18n.t('directionToast.duration')} ${minutes} min`);
        }, 1500);
    }

    static async centerBounds(coordinates: number[][]): Promise<void> {
        const map = this.mapObject;
        // La Directions API devuelve pares [lon, lat]; el tipo GeoJSON.Position
        // no garantiza la aridad, de ahi las aserciones.
        const bounds = new mapboxgl.LngLatBounds(
            coordinates[0] as [number, number],
            coordinates[0] as [number, number]
        );
        for (const coord of coordinates) {
            bounds.extend(coord as [number, number]);
        }

        map.fitBounds(bounds, {
            padding: 20
        });
    }

    /**
     * Removes the 'selected' classes from accommodation and point
     * of interest markers to clean out of map markers.
     */
    static removeSelectedPlaces() {
        document.querySelectorAll('.mapboxgl-marker.accommodation.selected, .mapboxgl-marker.poi.selected')
            .forEach(marker => marker.classList.remove('selected'));
    }

}

export {
    MapService,
    mapLayers,
};

export default MapService;
